import { useEffect, useState } from 'react';

/**
 * Whether a CSS media query matches, following it as the window changes.
 *
 * For layout decisions that change MARKUP, not just styles -- a CSS
 * `@media` rule can hide a block but cannot give it a different control.
 * Anything a stylesheet can do on its own belongs in the stylesheet.
 *
 * `false` where `matchMedia` is missing (an old engine, a test without
 * the stub in `tests/setup.ts`), which is the "nothing special about
 * this screen" answer.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => readMatch(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(query);
    } catch {
      return;
    }
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);

  return matches;
}

function readMatch(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}
